import React, { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { setAuthToken, setWorkspaceId, getAuthToken, apiClient } from '@/services/api';
import { integrationsApi } from '@/services/api/integrationApi';
import { ApiRequestError } from '@/services/api/core';
import { AuthFormCard, AuthPageLayout } from '@/layouts/AuthPageLayout';
import { ShopifyHandoffNotice } from './signup/ShopifyHandoffNotice';
import { ShopifyConnectExisting } from './signup/ShopifyConnectExisting';
import { MarketingOptIn } from './signup/MarketingOptIn';
import { LegalAgreement } from './signup/LegalAgreement';
import { rememberShopifyHandoff } from '../lib/shopifyHandoff';
import { startGoogleSignIn } from '../lib/googleSignIn';
import { shopifyConnectedDestination } from '../lib/shopifyConnectedDestination';
import api from '../services/api';
import { browserZone } from '../lib/browserZone';
import { businessTypeFromPlaybookKind } from './signup/playbookKind';
import { t } from '@/i18n';
import { toast } from '../stores/toastStore';
import {
  AuthButton,
  AuthCheckbox,
  AuthDivider,
  AuthGoogleButton,
  AuthInput,
  AuthLabel,
  FieldError,
} from './auth/authFormControls';
import {
  PASSWORD_RULES,
  validateAgreeToTerms,
  validateEmail,
  validateName,
  validatePassword,
} from './auth/authValidation';

interface SignupFormData {
  name: string;
  email: string;
  password: string;
  businessType: string;
  agreeToTerms: boolean;
  marketingOptIn: boolean;
}

type SignupField = 'name' | 'email' | 'password' | 'agreeToTerms';
type FieldErrors = Partial<Record<SignupField, string>>;
type BusinessKindOption = { value: string; label: string };

const BUSINESS_KIND_OPTIONS: BusinessKindOption[] = [
  { value: 'ecommerce', label: 'Online store' },
  { value: 'service', label: 'Service business' },
  { value: 'coaching', label: 'Coaching / consulting' },
  { value: 'course', label: 'Courses / community' },
  { value: 'saas', label: 'SaaS / software' },
  { value: 'content_creator', label: 'Content creator / influencer' },
];

function validateSignup(data: SignupFormData): FieldErrors {
  const next: FieldErrors = {};
  const nameErr = validateName(data.name);
  const emailErr = validateEmail(data.email);
  const passwordErr = validatePassword(data.password);
  const termsErr = validateAgreeToTerms(data.agreeToTerms);
  if (nameErr) next.name = nameErr;
  if (emailErr) next.email = emailErr;
  if (passwordErr) next.password = passwordErr;
  if (termsErr) next.agreeToTerms = termsErr;
  return next;
}

export const Signup: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const shopifyStatus = searchParams.get('shopify');
  const shopifyShop = searchParams.get('shop');
  const shopifyName = searchParams.get('name');
  const shopifyReason = searchParams.get('reason');

  const [formData, setFormData] = useState<SignupFormData>({
    name: '',
    email: '',
    password: '',
    businessType: shopifyStatus === 'connected' ? 'ecommerce' : businessTypeFromPlaybookKind(searchParams.get('kind')),
    agreeToTerms: false,
    marketingOptIn: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [showPassword, setShowPassword] = useState(false);

  const signedInHandoff = shopifyStatus === 'connected' && !!shopifyShop && !!getAuthToken();

  /** Clear a field's error as soon as the user starts correcting it. */
  const clearFieldError = (field: SignupField): void => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const nextErrors = validateSignup(formData);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiClient.post<{ success: boolean; data: { token: string; workspace?: { id: string } } }>('/auth/register', {
        name: formData.name.trim(),
        email: formData.email.trim(),
        password: formData.password,
        consent: formData.agreeToTerms,
        marketingOptIn: formData.marketingOptIn,
        ...browserZone(),
        ...(formData.businessType
          ? { businessType: formData.businessType }
          : shopifyStatus === 'connected' ? { businessType: 'ecommerce' } : {}),
      });

      const payload = response.data ?? response;
      setAuthToken(payload.token);
      if (payload.workspace) {
        setWorkspaceId(payload.workspace.id);
      }

      toast.success('Account created successfully.');

      if (shopifyStatus === 'connected' && shopifyShop) {
        try {
          await integrationsApi.shopifyFinalize(shopifyShop);
          navigate('/onboarding?platform=shopify&success=true');
        } catch {
          navigate('/settings/integrations?shopify=error&reason=finalize_failed');
        }
        return;
      }

      if (shopifyStatus === 'pending' && shopifyShop) {
        navigate(`/onboarding?shopify=expired&shop=${encodeURIComponent(shopifyShop)}`);
        return;
      }

      navigate('/business');
    } catch (err) {
      let msg: string;
      if (err instanceof ApiRequestError) {
        const detail = err.details?.[0];
        msg = detail?.message || err.message || 'Registration failed. Please try again.';
      } else if (err instanceof Error) {
        msg = err.message;
      } else {
        msg = 'Registration failed. Please try again.';
      }
      toast.error(msg);
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    clearFieldError(name as SignupField);
  };

  return (
    <AuthPageLayout>
      <AuthFormCard>
        {!signedInHandoff && (
          <div className="mb-8 text-center">
            <h1 className="mb-2 text-2xl font-bold tracking-tight text-gray-900">Create your account</h1>
            <p className="text-sm text-gray-500">{t('signup.subtitle')}</p>
          </div>
        )}

        {signedInHandoff && shopifyShop ? (
          <ShopifyConnectExisting
            shop={shopifyShop}
            name={shopifyName}
            onConnected={() => { void shopifyConnectedDestination(() => api.settings.getBrand()).then((to) => navigate(to)); }}
            onFailed={() => navigate('/account?tab=integrations&shopify=error&reason=finalize_failed')}
          />
        ) : (
          <ShopifyHandoffNotice status={shopifyStatus} shop={shopifyShop} name={shopifyName} reason={shopifyReason} />
        )}

        {!signedInHandoff && (
          <>
            <form onSubmit={handleSubmit} className="space-y-5" data-float-avoid noValidate>
              <div className="space-y-2">
                <AuthLabel htmlFor="name" required>Full name</AuthLabel>
                <AuthInput
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="John Doe"
                  disabled={isLoading}
                  error={!!errors.name}
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? 'name-error' : undefined}
                />
                <FieldError id="name-error" message={errors.name} />
              </div>

              <div className="space-y-2">
                <AuthLabel htmlFor="email" required>Email</AuthLabel>
                <AuthInput
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="you@example.com"
                  disabled={isLoading}
                  error={!!errors.email}
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                />
                <FieldError id="email-error" message={errors.email} />
              </div>

              <div className="space-y-2">
                <AuthLabel htmlFor="businessType">What kind of business?</AuthLabel>
                <select
                  id="businessType"
                  name="businessType"
                  value={formData.businessType}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  className={cn(
                    'flex h-11 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm transition-all',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-0',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                    formData.businessType ? 'text-gray-900' : 'text-gray-400',
                  )}
                >
                  <option value="">I&apos;ll decide later</option>
                  {BUSINESS_KIND_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <AuthLabel htmlFor="password" required>Password</AuthLabel>
                <div className="relative">
                  <AuthInput
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Create a password"
                    disabled={isLoading}
                    className="pr-10"
                    error={!!errors.password}
                    aria-invalid={!!errors.password}
                    aria-describedby="password-rules"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    disabled={isLoading}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <FieldError id="password-error" message={errors.password} />
                <ul id="password-rules" className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  {PASSWORD_RULES.map((rule) => {
                    const passed = rule.test(formData.password);
                    return (
                      <li
                        key={rule.id}
                        className={cn(
                          'flex items-center gap-1.5',
                          formData.password.length === 0 ? 'text-gray-500' : passed ? 'text-emerald-600' : 'text-gray-500',
                        )}
                      >
                        <Check
                          className={cn('h-3 w-3 shrink-0', formData.password.length > 0 && passed ? 'opacity-100' : 'opacity-30')}
                          strokeWidth={3}
                        />
                        <span>{rule.label}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="space-y-2">
                <div className="flex items-start space-x-2">
                  <AuthCheckbox
                    id="agreeToTerms"
                    checked={formData.agreeToTerms}
                    error={!!errors.agreeToTerms}
                    onCheckedChange={(checked) => {
                      setFormData((prev) => ({ ...prev, agreeToTerms: checked }));
                      clearFieldError('agreeToTerms');
                    }}
                    disabled={isLoading}
                    className="mt-0.5"
                  />
                  <AuthLabel
                    htmlFor="agreeToTerms"
                    className="cursor-pointer text-sm font-normal leading-tight text-gray-700"
                  >
                    <LegalAgreement template="signup.agreeToTerms" />
                  </AuthLabel>
                </div>
                <FieldError id="agreeToTerms-error" message={errors.agreeToTerms} />
              </div>

              <MarketingOptIn
                checked={formData.marketingOptIn}
                disabled={isLoading}
                onChange={(checked) => setFormData((prev) => ({ ...prev, marketingOptIn: checked }))}
              />

              <AuthButton type="submit" data-cookie-avoid disabled={isLoading} loading={isLoading} className="w-full">
                {isLoading ? 'Creating account...' : 'Create account'}
              </AuthButton>
            </form>

            <AuthDivider />

            <AuthGoogleButton
              label="Continue with Google"
              onClick={() => {
                rememberShopifyHandoff(shopifyStatus === 'connected' ? shopifyShop : null);
                void startGoogleSignIn();
              }}
            />

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                Already have an account?{' '}
                <Link
                  to={shopifyStatus === 'connected' ? `/login${location.search}` : '/login'}
                  className="font-semibold text-gray-900 hover:text-gray-600"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </>
        )}
      </AuthFormCard>
    </AuthPageLayout>
  );
};

export default Signup;
